import { Container, Sprite } from '../../../odie';

import { AxisId, ChainBlockProps } from '../../defs/block';
import { config } from '../../defs/config';
import { blockPositionView } from '../../util/blockTools';
import { FitWidthBlockSprite } from './FitWidthBlockSprite';

export const ChainBlockLayout = {
  Horizontal: 'HORIZONTAL',
  HorizontalAlternate: 'HORIZONTAL_ALTERNATE',
  Vertical: 'VERTICAL',
  VerticalAlternate: 'VERTICAL_ALTERNATE',
  CornerTopLeft: 'CORNER_TOP_LEFT',
  CornerTopRight: 'CORNER_TOP_RIGHT',
  CornerBottomLeft: 'CORNER_BOTTOM_LEFT',
  CornerBottomRight: 'CORNER_BOTTOM_RIGHT',
  TSectionTop: 'T_SECTION_TOP',
  TSectionRight: 'T_SECTION_RIGHT',
  TSectionBottom: 'T_SECTION_BOTTOM',
  TSectionLeft: 'T_SECTION_LEFT',
  Cross: 'CROSS',
};
export type ChainBlockLayoutType =
  (typeof ChainBlockLayout)[keyof typeof ChainBlockLayout];

type SectionType = AxisId | CornerId | JunctionId;

type CornerId =
  | 'cornerTopLeft'
  | 'cornerTopRight'
  | 'cornerBottomLeft'
  | 'cornerBottomRight';

type JunctionId =
  | 'tsectionTop'
  | 'tsectionBottom'
  | 'tsectionLeft'
  | 'tsectionRight'
  | 'cross';

const manifest: Record<SectionType, string> = {
  vertical: 'barbedwire/block.barbedwire.vertical.png',
  horizontal: 'barbedwire/block.barbedwire.horizontal.png',
  cornerTopLeft: 'barbedwire/block.barbedwire.top.left.png',
  cornerTopRight: 'barbedwire/block.barbedwire.top.right.png',
  cornerBottomLeft: 'barbedwire/block.barbedwire.bottom.left.png',
  cornerBottomRight: 'barbedwire/block.barbedwire.bottom.right.png',
  tsectionTop: 'barbedwire/block.barbedwire.t.top.png',
  tsectionBottom: 'barbedwire/block.barbedwire.t.bottom.png',
  tsectionLeft: 'barbedwire/block.barbedwire.t.left.png',
  tsectionRight: 'barbedwire/block.barbedwire.t.right.png',
  cross: 'barbedwire/block.barbedwire.cross.png',
};

const TILE_SIZE = config.tile.size;
const HALF_TILE_SIZE = TILE_SIZE / 2;
const Y_OFFSET = 4;

export const isLineLayout = (layout: ChainBlockLayoutType): boolean =>
  [
    ChainBlockLayout.Horizontal,
    ChainBlockLayout.Vertical,
    ChainBlockLayout.HorizontalAlternate,
    ChainBlockLayout.VerticalAlternate,
  ].includes(layout);

const isVerticalLineLayout = (layout: ChainBlockLayoutType): boolean =>
  [ChainBlockLayout.Vertical, ChainBlockLayout.VerticalAlternate].includes(
    layout,
  );

const isCornerLayout = (layout: ChainBlockLayoutType): boolean =>
  [
    ChainBlockLayout.CornerTopLeft,
    ChainBlockLayout.CornerTopRight,
    ChainBlockLayout.CornerBottomLeft,
    ChainBlockLayout.CornerBottomRight,
  ].includes(layout);

const isJunctionLayout = (layout: ChainBlockLayoutType): boolean =>
  [
    ChainBlockLayout.TSectionTop,
    ChainBlockLayout.TSectionRight,
    ChainBlockLayout.TSectionBottom,
    ChainBlockLayout.TSectionLeft,
    ChainBlockLayout.Cross,
  ].includes(layout);

const getCornerId = (layout: ChainBlockLayoutType): CornerId | null => {
  switch (layout) {
    case ChainBlockLayout.CornerTopLeft:
      return 'cornerTopLeft';
    case ChainBlockLayout.CornerTopRight:
      return 'cornerTopRight';
    case ChainBlockLayout.CornerBottomLeft:
      return 'cornerBottomLeft';
    case ChainBlockLayout.CornerBottomRight:
      return 'cornerBottomRight';
    default:
      return null;
  }
};

const getJunctionId = (layout: ChainBlockLayoutType): JunctionId | null => {
  switch (layout) {
    case ChainBlockLayout.TSectionTop:
      return 'tsectionTop';
    case ChainBlockLayout.TSectionRight:
      return 'tsectionRight';
    case ChainBlockLayout.TSectionBottom:
      return 'tsectionBottom';
    case ChainBlockLayout.TSectionLeft:
      return 'tsectionLeft';
    case ChainBlockLayout.Cross:
      return 'cross';
    default:
      return null;
  }
};

/*
    chain block view
*/
export class ChainBlockView extends Container {
  public leavesView = new Container();
  private _damage = 0;
  private _layout: ChainBlockLayoutType = ChainBlockLayout.Horizontal;
  private _block?: Sprite;
  private _leaves: Sprite[] = [];

  public static assets(): string[] {
    return Object.values(manifest);
  }

  constructor(props: ChainBlockProps) {
    super();
    this._updateLayout();
    blockPositionView(this, props);
  }

  public get damage(): number {
    return this._damage;
  }

  public set damage(value: number) {
    if (this._damage === value) {
      return;
    }

    this._damage = value;
    this._updateLeaves();
  }

  public get layout(): ChainBlockLayoutType {
    return this._layout;
  }

  public set layout(chainLayout: ChainBlockLayoutType) {
    if (this._layout === chainLayout) {
      return;
    }

    this._layout = chainLayout;

    this._updateLayout();
  }

  private _updateLayout() {
    this._despawnBlock();
    this._spawnBlock();
  }

  public getActiveLeafIndex(): number {
    // A chain block can potentially briefly sustain more than 2 damage if the same
    // block is attacked 3 times in a row without other chain blocks being hit.
    const maxDamageIndex = config.blocks.chain.damage - 1;
    const damageIndex = Math.min(this._damage, maxDamageIndex);
    return maxDamageIndex - damageIndex;
  }

  private _updateLeaves() {
    this._leaves.forEach(
      (leaf, index) => (leaf.visible = index <= this.getActiveLeafIndex()),
    );
  }

  private _getBlockSprite(sectionType: SectionType): Sprite {
    const layer = new FitWidthBlockSprite(manifest[sectionType]);
    layer.pivotX = layer.width / 2;
    layer.pivotY = layer.height / 2;

    return layer;
  }

  private _getLeaf(index: number): Sprite {
    const layer = new FitWidthBlockSprite(
      `barbedwire/block.barbedwire.leaf${index}.png`,
    );

    return layer;
  }

  private _getLineBlock(axisId: AxisId): Sprite {
    const lineBlock = this._getBlockSprite(axisId);
    lineBlock.x = HALF_TILE_SIZE;
    lineBlock.y = HALF_TILE_SIZE + Y_OFFSET;

    return lineBlock;
  }

  private _getCorner(cornerId: CornerId): Sprite {
    const postBlock = this._getBlockSprite(cornerId);
    postBlock.x = HALF_TILE_SIZE;
    postBlock.y = HALF_TILE_SIZE + Y_OFFSET;

    return postBlock;
  }

  private _getJunction(junctionId: JunctionId): Sprite {
    const postBlock = this._getBlockSprite(junctionId);
    postBlock.x = HALF_TILE_SIZE;
    postBlock.y = HALF_TILE_SIZE + Y_OFFSET;

    return postBlock;
  }

  private _spawnBlock() {
    const axisId = isVerticalLineLayout(this._layout)
      ? 'vertical'
      : 'horizontal';
    const cornerId = getCornerId(this._layout);
    const junctionId = getJunctionId(this._layout);

    if (isLineLayout(this._layout)) {
      this._block = this._getLineBlock(axisId);
    } else if (isCornerLayout(this._layout)) {
      this._block = this._getCorner(cornerId!);
    } else {
      this._block = this._getJunction(junctionId!);
    }

    this.addChild(this._block);
    this._spawnLeaves(axisId === 'horizontal' && !cornerId);
  }

  private _spawnLeaves(isAlternate: boolean) {
    const amount = config.blocks.chain.damage;
    const rotation = isAlternate ? -Math.PI * 0.5 : 0;

    for (let index = 0; index < amount; index++) {
      const leaf = this._getLeaf(index);
      leaf.rotation = rotation;
      this._leaves.push(leaf);
      this.leavesView.addChild(leaf);
    }

    this._updateLeaves();
    this.addChild(this.leavesView);
  }

  private _despawnBlock() {
    if (!this._block) return;
    this.removeChild(this._block);
    this.removeChild(this.leavesView);
    this._leaves.forEach((leaf) => this.leavesView.removeChild(leaf));
    this._leaves.length = 0;
    this._block = undefined;
  }
}
